import React from 'react';

import type { ExtensionHandlers, ExtensionParams } from '@atlaskit/editor-common/extensions';

import { ExtensionMode, SPOTLIGHT_EXTENSION_TYPE } from '@atlassian/company-hub-common';
import { CARDS_EXTENSION_TYPE } from '@atlassian/cards-extension/constants';
import { type CardsExtensionParameters } from '@atlassian/cards-extension/types';
import {
	SEARCH_EXTENSION_TYPE,
	type SearchExtensionParameters,
} from '@atlassian/custom-search-extension';
import {
	LINKS_MENU_EXTENSION_TYPE,
	type LinksMenuParameters,
} from '@atlassian/links-menu-extension';
import type { SpotlightParameters } from '@atlassian/spotlight-extension';
import { carouselExtensionType, type CarouselParameters } from '@atlassian/carousel-extension';

import { LoadablePaint } from '@confluence/loadable';

import { HUB_ELEMENT_PLACEHOLDER_EXTENSION_TYPE } from '@atlassian/hub-element-placeholder';
import { ConfluenceDependenciesProvider } from '../platform-props-providers/ConfluenceDependenciesProvider.tsx';

export const CardsExtensionLoader = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CardsExtension" */ '@atlassian/cards-extension'))
			.CardsExtension,
});

const PlatformSpotlightExtensionLoader = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-spotlight-extension" */ '@atlassian/spotlight-extension'
			)
		).SpotlightExtension,
});

const PlatformSearchExtensionLoader = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PlatformSearchExtension" */ '@atlassian/custom-search-extension'
			)
		).CustomSearchExtension,
});

const LinksMenuLoader = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PlatformLinksMenuExtension" */ '@atlassian/links-menu-extension'
			)
		).LinksMenu,
});

const CarouselLoader = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PlatformCarouselExtension" */ '@atlassian/carousel-extension'
			)
		).Carousel,
});

type getRendererCustomSitesExtensionHandlersProps = {
	contentId: string | undefined;
};

export const getRendererCustomSitesExtensionHandlers = ({
	contentId,
}: getRendererCustomSitesExtensionHandlersProps): ExtensionHandlers => {
	const extensionHandlers: ExtensionHandlers = {
		[CARDS_EXTENSION_TYPE]: () => <></>,
		[carouselExtensionType]: () => <></>,
		[LINKS_MENU_EXTENSION_TYPE]: () => <></>,
		[HUB_ELEMENT_PLACEHOLDER_EXTENSION_TYPE]: () => <></>,
		[SPOTLIGHT_EXTENSION_TYPE]: () => <></>,
		[SEARCH_EXTENSION_TYPE]: () => <></>,
	};

	if (contentId) {
		extensionHandlers[CARDS_EXTENSION_TYPE] = (
			extension: ExtensionParams<CardsExtensionParameters>,
		) => (
			<CardsExtensionLoader
				extensionNode={extension}
				contentId={contentId}
				mode={ExtensionMode.RENDERER}
				propsProvider={ConfluenceDependenciesProvider}
			/>
		);
		extensionHandlers[carouselExtensionType] = (extension: ExtensionParams<CarouselParameters>) => (
			<CarouselLoader
				extensionNode={extension}
				contentId={contentId}
				propsProvider={ConfluenceDependenciesProvider}
			/>
		);
		extensionHandlers[LINKS_MENU_EXTENSION_TYPE] = (
			extension: ExtensionParams<LinksMenuParameters>,
		) => (
			<LinksMenuLoader
				mode={ExtensionMode.RENDERER}
				extensionNode={extension}
				contentId={contentId}
				propsProvider={ConfluenceDependenciesProvider}
			/>
		);
		extensionHandlers[SPOTLIGHT_EXTENSION_TYPE] = (
			extension: ExtensionParams<SpotlightParameters>,
		) => (
			<PlatformSpotlightExtensionLoader
				contentId={contentId}
				extensionNode={extension}
				mode={ExtensionMode.RENDERER}
				propsProvider={ConfluenceDependenciesProvider}
			/>
		);
		extensionHandlers[HUB_ELEMENT_PLACEHOLDER_EXTENSION_TYPE] = () => null;
		extensionHandlers[SEARCH_EXTENSION_TYPE] = (
			extension: ExtensionParams<SearchExtensionParameters>,
		) => (
			<PlatformSearchExtensionLoader
				mode={ExtensionMode.RENDERER}
				contentId={contentId}
				extensionNode={extension}
				propsProvider={ConfluenceDependenciesProvider}
			/>
		);
	}
	return extensionHandlers;
};
